import { useCallback, useState } from "react";
import PageRecord from "models/view-models/page-record";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import CopyPageService, {
    ClonePageBodyParams,
} from "utilities/services/copy-pages/copy-page-service";

interface UseClonePagesOptions {
    onSuccess?: (clonedPage: PageRecord) => void;
    onError?: (errors: string[]) => void;
}

interface UseClonePages {
    clonePage: (params: ClonePageBodyParams) => Promise<PageRecord | null>;
    clonePageAndAddToNotebook: (
        params: ClonePageBodyParams
    ) => Promise<PageRecord | null>;
    errors: string[];
    loading: boolean;
}

export default function useClonePages(
    options?: UseClonePagesOptions
): UseClonePages {
    const { onSuccess, onError } = options || {};
    const { addPageToNotebook } = useNotebooksAtom();
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const { create: clonePageApi } = CopyPageService.useClone();

    const clonePage = useCallback(
        async (params: ClonePageBodyParams): Promise<PageRecord | null> => {
            setLoading(true);
            setErrors([]);

            try {
                const result = await clonePageApi(params);
                const clonedPage = result.resultObject;

                if (result.result?.hasErrors() || !clonedPage) {
                    const errorMessages = result.result?.errors?.map(
                        (error) => error.message || "Failed to clone page"
                    ) || ["Failed to clone page"];
                    setErrors(errorMessages);
                    onError?.(errorMessages);
                    return null;
                }

                onSuccess?.(clonedPage);
                return clonedPage;
            } catch (error) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : "An unexpected error occurred while cloning the page";
                const errorMessages = [errorMessage];
                setErrors(errorMessages);
                onError?.(errorMessages);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [clonePageApi, onSuccess, onError]
    );

    const clonePageAndAddToNotebook = useCallback(
        async (params: ClonePageBodyParams): Promise<PageRecord | null> => {
            const clonedPage = await clonePage(params);

            if (clonedPage == null) {
                return null;
            }

            addPageToNotebook(clonedPage.notebookId, clonedPage);

            return clonedPage;
        },
        [clonePage, addPageToNotebook]
    );

    return {
        clonePage,
        clonePageAndAddToNotebook,
        errors,
        loading,
    };
}
